import type { RawParams } from "@uirouter/core";

import type { CustomAttribute, ITrack, Primitive, TrackingEvent } from "@similarweb/sw-track";

import trackingParamAliases from "app/pages/sales-intelligence/constants/tracking-params";
import type { CustomTrackingParams, UserActivityData } from "app/pages/sales-intelligence/types/customTrackingParams";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";
import { isSalesExtensionPresent } from "app/pages/sales-intelligence/utils/extension";
import type { PageViewTrackers } from "app/services/track/AllTrackersService";
import { AllTrackersBaseService } from "app/services/track/AllTrackersService";
import { markPageViewTracked } from "app/services/track/pageViewTrackSignal";
import { getPageViewDropContextFromState, reportDroppedPageView } from "app/services/track/reportDroppedPageView";
import { getPageViewTrackingParams } from "app/services/track/utils/getPageViewTrackingParams";
import type { IRouterState, RouterTrackingId } from "single-spa/react-app/router/IRouterState";
import { resolveRouterTrackingId } from "single-spa/react-app/router/resolveRouterTrackingId";

type UserActivity = Partial<UserActivityData> | null;

class SIAllTrackersService extends AllTrackersBaseService {
    protected customTrackingParams: Partial<CustomTrackingParams> | null = null;
    public static trackingParams: string[] = Object.values(trackingParamAliases);
    private readonly reportedTrackingFailures = new Set<string>();

    constructor(trackers: ITrack[], pageViewTrackers: PageViewTrackers) {
        super(trackers, pageViewTrackers);

        if (window.similarweb?.userActivityData) {
            this.setCustomTrackingParams(window.similarweb.userActivityData);
        }
    }

    setCustomTrackingParams(data: Partial<UserActivity>): void {
        if (!data || typeof data !== "object") {
            return;
        }

        const params = Object.keys(data).reduce(
            (acum: Partial<CustomTrackingParams>, key: keyof UserActivityData) => ({
                ...acum,
                [trackingParamAliases[key]]: data[key],
            }),
            {},
        );

        this.customTrackingParams = this.customTrackingParams
            ? {
                  ...this.customTrackingParams,
                  ...params,
              }
            : params;
    }

    isTrackingDataValid(): boolean {
        return (
            this.customTrackingParams !== null &&
            typeof this.customTrackingParams === "object" &&
            Object.keys(this.customTrackingParams).length > 0
        );
    }

    dispatchTrackEvent(
        event: TrackingEvent,
        action: string,
        eventName: string,
        customAttributes?: Record<CustomAttribute, Primitive>,
    ): void {
        const customData = {
            ...(this.customTrackingParams || {}),
            ...(customAttributes || {}),
        };

        try {
            super.dispatchTrackEvent(event, action, eventName, customData);
        } catch (error) {
            // Trackers reach third-party globals (a patched console.warn has thrown here in production).
            // Analytics must never propagate into the React event handler that triggered it.
            this.reportTrackingFailure(error);
        }
    }

    private reportTrackingFailure(error: unknown): void {
        const cause = error instanceof Error ? error : new Error(String(error));
        const signature = `${cause.name}|${cause.message}`;

        if (this.reportedTrackingFailures.has(signature)) {
            return;
        }

        this.reportedTrackingFailures.add(signature);

        // This runs only when tracking is already broken; it must not become the next crash.
        try {
            trackError(
                new SiError(
                    "Tracking event dispatch failed",
                    {
                        category: errorCategoryMap.service,
                        component: "SIAllTrackersService",
                        operation: "dispatchTrackEvent",
                    },
                    { cause },
                ),
            );
        } catch {
            /* reporting is best-effort */
        }
    }

    public async trackPageViewWithCustomUrl(toState: IRouterState, toParams: RawParams) {
        // Must run synchronously before the first await: most handlers call SwTrack.trackPageView
        // without awaiting, so markPageViewTracked() must run in the same tick as the handler call.
        markPageViewTracked();

        const trackingParams = await getPageViewTrackingParams(toState, toParams);

        if (trackingParams == null) {
            reportDroppedPageView({
                reason: "tracking_params_null",
                ...getPageViewDropContextFromState(toState, toParams),
            });
            return;
        }

        const trackingId = resolveRouterTrackingId(toState, toParams);

        const params = {
            ...trackingParams,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            custom_data: {
                ...(this.customTrackingParams || {}),
                ...((trackingParams.custom_data as Record<string, unknown> | undefined) || {}),
                ...(((trackingId as RouterTrackingId | undefined)?.pageCustomData as
                    | Record<string, unknown>
                    | undefined) || {}),
                // eslint-disable-next-line @typescript-eslint/naming-convention
                si_extension: isSalesExtensionPresent(),
            },
        };

        this.trackPageViewWithTrackingParams(params);
    }
}

export default SIAllTrackersService;
